import booleanPointInPolygon from '@turf/boolean-point-in-polygon'
import { Feature, Polygon, point, polygon } from '@turf/helpers'
import { CellInfoDimension, PlotSelectionState, ScatterPlotMetadata } from 'redux/slices/types'
import { ColumnTable } from 'arquero'

import { CellInfo } from '../tsv/types'

import { Combination } from './generateCellInfoCombinations'
import { getColumn } from '../arrow/arqueroHelpers'

interface EnhanceSelectionParams {
  selection: PlotSelectionState
  cellsData: CellInfo[]
  getDataForCell: (cell: CellInfo) => {
    isRemoved: boolean
    isHidden: boolean
    splitOutput?: Combination[]
  }
  scatterPlotMetaData: ScatterPlotMetadata
  visibleSelectedComparisonDimensions: CellInfoDimension[] | undefined
  isPreFilter: boolean
}

interface EnhanceSelectionParamsDataFrame {
  selection: PlotSelectionState
  dataFrame: ColumnTable
  prefilteredCellIdSet: Set<string>
  scatterPlotMetaData: ScatterPlotMetadata
  visibleSelectedComparisonDimensions: CellInfoDimension[] | undefined
  isPreFilter: boolean
}

/**
 * Enhance selection with all cells using point-in-polygon detection
 * Uses geometric selection logic to find all cells within the selection polygon,
 * regardless of their visibility or filter state
 */
export default function enhanceSelectionWithGrayCells({
  selection,
  cellsData,
  getDataForCell,
  scatterPlotMetaData,
  visibleSelectedComparisonDimensions,
  isPreFilter,
}: EnhanceSelectionParams): PlotSelectionState {
  // Don't enhance in pre-filter mode
  if (isPreFilter || !cellsData) {
    return selection
  }

  const { lassoPoints, range } = selection
  const additionalPoints: typeof selection.points = []

  // Calculate plot offsets for multi-plot mode
  const isMultiPlotMode = visibleSelectedComparisonDimensions && visibleSelectedComparisonDimensions.length > 0
  const { minX, minY, maxX, maxY } = scatterPlotMetaData
  const xOffset = (maxX - minX) * 1.5
  const yOffset = (maxY - minY) * 1.5
  const numOfColumns = isMultiPlotMode ? visibleSelectedComparisonDimensions[0].values.length : 1

  // Create Turf polygon from selection
  type TurfPolygon = Feature<Polygon>
  let turfPolygon: TurfPolygon | null = null

  if (lassoPoints && lassoPoints.x.length > 0) {
    // Lasso selection - create polygon from lasso points
    const coordinates = lassoPoints.x.map((x, i) => [x, lassoPoints.y[i]] as [number, number])
    // Close the polygon by adding the first point at the end
    coordinates.push(coordinates[0])
    turfPolygon = polygon([coordinates])
  } else if (range && range.x && range.y) {
    const [boxMinX, boxMaxX] = [Math.min(...range.x), Math.max(...range.x)]
    const [boxMinY, boxMaxY] = [Math.min(...range.y), Math.max(...range.y)]
    turfPolygon = polygon([
      [
        [boxMinX, boxMinY],
        [boxMaxX, boxMinY],
        [boxMaxX, boxMaxY],
        [boxMinX, boxMaxY],
        [boxMinX, boxMinY],
      ],
    ])
  }

  if (!turfPolygon) {
    return selection
  }

  const selectionPolygon = turfPolygon

  // Create a set of existing points to avoid duplicates
  // Use position (x,y) + cellId as the key since same cell can appear at multiple positions in multi-plot
  const existingPointKeys = new Set(
    (selection.points || []).map((p) => `${p.x},${p.y},${p.cellId}`)
  )

  // Apply geometric selection to all cells in cellsData
  cellsData.forEach((cell) => {
    const { isRemoved, splitOutput } = getDataForCell(cell)

    // Skip removed cells
    if (isRemoved) return

    const cellX = +cell.UMAP_0
    const cellY = +cell.UMAP_1

    // Generate positions to check based on mode
    // In single plot mode: just the cell's original position
    // In multi-plot mode: cell appears in each subplot
    const numPositions = isMultiPlotMode && splitOutput ? splitOutput.length : 1

    for (let posIndex = 0; posIndex < numPositions; posIndex += 1) {
      // Calculate position coordinates
      const col = posIndex % numOfColumns
      const row = Math.floor(posIndex / numOfColumns)
      const x = cellX + col * xOffset
      const y = cellY - row * yOffset

      // Check if this position is within the selection polygon
      const cellPoint = point([x, y])
      if (booleanPointInPolygon(cellPoint, selectionPolygon)) {
        const pointKey = `${x},${y},${cell.CELL_ID}`
        // Only add if this exact position+cell combination isn't already in Plotly's selection
        if (!existingPointKeys.has(pointKey)) {
          additionalPoints.push({
            x,
            y,
            originalX: cellX,
            originalY: cellY,
            cellId: cell.CELL_ID,
          })
          existingPointKeys.add(pointKey)
        }
      }
    }
  })

  // Return enhanced selection with all matched cell points
  return {
    ...selection,
    points: [...(selection.points || []), ...additionalPoints],
  }
}

/**
 * DataFrame version - Enhance selection with all cells using point-in-polygon detection
 * Uses geometric selection logic to find all cells within the selection polygon,
 * regardless of their visibility or filter state
 * Memory-efficient: operates directly on DataFrame columns instead of creating object arrays
 */
export function enhanceSelectionWithGrayCellsFromDataFrame({
  selection,
  dataFrame,
  prefilteredCellIdSet,
  scatterPlotMetaData,
  visibleSelectedComparisonDimensions,
  isPreFilter,
}: EnhanceSelectionParamsDataFrame): PlotSelectionState {
  // Don't enhance in pre-filter mode
  if (isPreFilter || !dataFrame) {
    return selection
  }

  const { lassoPoints, range } = selection
  const additionalPoints: typeof selection.points = []

  // Calculate plot offsets for multi-plot mode
  const isMultiPlotMode = visibleSelectedComparisonDimensions && visibleSelectedComparisonDimensions.length > 0
  const { minX, minY, maxX, maxY } = scatterPlotMetaData
  const xOffset = (maxX - minX) * 1.5
  const yOffset = (maxY - minY) * 1.5
  const numOfColumns = isMultiPlotMode ? visibleSelectedComparisonDimensions[0].values.length : 1

  // Calculate number of positions for multi-plot mode (product of dimension values)
  const numPositions = isMultiPlotMode
    ? visibleSelectedComparisonDimensions[0].values.length *
      (visibleSelectedComparisonDimensions[1]?.values.length || 1)
    : 1

  // Create Turf polygon from selection
  type TurfPolygon = Feature<Polygon>
  let turfPolygon: TurfPolygon | null = null

  if (lassoPoints && lassoPoints.x.length > 0) {
    // Lasso selection - create polygon from lasso points
    const coordinates = lassoPoints.x.map((x, i) => [x, lassoPoints.y[i]] as [number, number])
    // Close the polygon by adding the first point at the end
    coordinates.push(coordinates[0])
    turfPolygon = polygon([coordinates])
  } else if (range && range.x && range.y) {
    const [boxMinX, boxMaxX] = [Math.min(...range.x), Math.max(...range.x)]
    const [boxMinY, boxMaxY] = [Math.min(...range.y), Math.max(...range.y)]
    turfPolygon = polygon([
      [
        [boxMinX, boxMinY],
        [boxMaxX, boxMinY],
        [boxMaxX, boxMaxY],
        [boxMinX, boxMaxY],
        [boxMinX, boxMinY],
      ],
    ])
  }

  if (!turfPolygon) {
    return selection
  }

  const selectionPolygon = turfPolygon

  // Create a set of existing points to avoid duplicates
  // Use position (x,y) + cellId as the key since same cell can appear at multiple positions in multi-plot
  const existingPointKeys = new Set(
    (selection.points || []).map((p) => `${p.x},${p.y},${p.cellId}`)
  )

  // Extract columns from DataFrame (cached to prevent redundant .toArray() calls)
  const cellIdColumn = getColumn<string>(dataFrame, 'CELL_ID')
  const umapXColumn = getColumn<number>(dataFrame, 'UMAP_0')
  const umapYColumn = getColumn<number>(dataFrame, 'UMAP_1')

  if (cellIdColumn.length === 0 || umapXColumn.length === 0 || umapYColumn.length === 0) {
    return selection
  }

  // Apply geometric selection to all cells in DataFrame
  // NO OBJECT CONVERSION - work directly with column arrays
  for (let i = 0; i < cellIdColumn.length; i += 1) {
    const cellId = cellIdColumn[i]

    // Skip prefiltered (removed) cells - check directly against set
    if (prefilteredCellIdSet.has(cellId)) {
      // eslint-disable-next-line no-continue
      continue
    }

    const cellX = umapXColumn[i]
    const cellY = umapYColumn[i]

    // Check all positions for this cell (1 for single-plot, multiple for multi-plot)
    for (let posIndex = 0; posIndex < numPositions; posIndex += 1) {
      // Calculate position coordinates
      const col = posIndex % numOfColumns
      const row = Math.floor(posIndex / numOfColumns)
      const x = cellX + col * xOffset
      const y = cellY - row * yOffset

      // Check if this position is within the selection polygon
      const cellPoint = point([x, y])
      if (booleanPointInPolygon(cellPoint, selectionPolygon)) {
        const pointKey = `${x},${y},${cellId}`
        // Only add if this exact position+cell combination isn't already in Plotly's selection
        if (!existingPointKeys.has(pointKey)) {
          additionalPoints.push({
            x,
            y,
            originalX: cellX,
            originalY: cellY,
            cellId,
          })
          existingPointKeys.add(pointKey)
        }
      }
    }
  }

  // Return enhanced selection with all matched cell points
  return {
    ...selection,
    points: [...(selection.points || []), ...additionalPoints],
  }
}
